/**
 * One node: its place in the tree, its owner, and the grants written on it.
 *
 * `owner_id` is required and non-null from the start so §2.6's blocking logic
 * has something to check against — which is why reassignment lives here as well
 * as inside the deactivation flow. Doing it here is `admin.override`, and it is
 * logged as an override rather than as the ordinary action (§6.2): an admin
 * moving ownership outside offboarding should read as unusual months later.
 */

import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Form } from 'react-aria-components';
import { useNode, usePeoplePicker, useReassignNode, useSpace } from '../../api/queries.ts';
import { formatDateTime } from '../../components/format.ts';
import { GrantsPanel } from '../../components/grants-panel.tsx';
import { Badge, roleLabel, StatusBadge } from '../../components/ui/badge.tsx';
import { Button, LinkButton } from '../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../components/ui/dialog.tsx';
import { Alert, ErrorState, Loading, PageError } from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';
import { Card, Definition, DefinitionList, Mono, PageHeader } from '../../components/ui/layout.tsx';

function ReassignForm({ nodeId, onClose }: { nodeId: string; onClose: () => void }) {
  const reassign = useReassignNode(nodeId);
  const picker = usePeoplePicker();
  const [ownerId, setOwnerId] = useState('');
  const [reason, setReason] = useState('');

  const options = (picker.data?.people ?? []).map((person) => ({
    value: person.id,
    label: person.name,
    description: `${person.email} · ${roleLabel(person.role)}`,
  }));

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        reassign.mutate(
          { ownerId, ...(reason.trim() ? { reason: reason.trim() } : {}) },
          { onSuccess: onClose },
        );
      }}
    >
      <DialogBody>
        <Alert tone="warning" title="This is an administrative override">
          It is recorded as <span className="font-mono">admin.ownership_reassigned</span>, not as an
          ordinary edit, and the reason is kept with it.
        </Alert>
        <Select
          label="New owner"
          placeholder="Choose a person…"
          options={options}
          selectedKey={ownerId || null}
          onSelectionChange={setOwnerId}
          isRequired
          description="Only active people can own a node."
        />
        <TextField label="Reason" value={reason} onChange={setReason} multiline rows={2} />
        {reassign.isError ? <ErrorState error={reassign.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isPending={reassign.isPending}
          isDisabled={!ownerId}
        >
          Reassign ownership
        </Button>
      </DialogFooter>
    </Form>
  );
}

export function NodeDetailPage() {
  const { nodeId } = useParams({ from: '/_authed/_employee/nodes/$nodeId' });
  const query = useNode(nodeId);
  const [reassigning, setReassigning] = useState(false);

  const spaceId = query.data?.node.spaceId;
  const space = useSpace(spaceId ?? '');

  if (query.isPending) return <Loading />;
  if (query.isError) {
    return (
      <PageError
        error={query.error}
        title="Node"
        back={{ href: '/schema/spaces', label: 'Back to spaces' }}
      />
    );
  }

  const { node, children, owner } = query.data;

  return (
    <>
      <PageHeader
        breadcrumb={
          <span className="flex items-center gap-1.5">
            <Link to="/schema/spaces" className="text-tertiary no-underline">
              Spaces
            </Link>
            <span aria-hidden="true">/</span>
            <Link
              to="/spaces/$spaceId"
              params={{ spaceId: node.spaceId }}
              className="text-tertiary no-underline"
            >
              {space.data?.space.name ?? 'Space'}
            </Link>
          </span>
        }
        title={node.title}
        actions={
          <>
            {/* The tree is how a document is browsed to; this is the step from
                the node to the thing it holds (§7.4). Only a document has one —
                a file's bytes and a link's URL are shown on this page itself. */}
            {node.type === 'document' ? (
              <LinkButton variant="primary" href={`/documents/${node.id}`}>
                Open document
              </LinkButton>
            ) : null}
            <LinkButton variant="secondary" href={`/access/nodes/${node.id}`}>
              Who can reach this
            </LinkButton>
            <Button variant="secondary" onPress={() => setReassigning(true)}>
              Reassign owner
            </Button>
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="mb-4 text-md font-semibold text-primary">Node</h2>
          <DefinitionList>
            <Definition term="Type">
              <Badge tone="outline">{node.type}</Badge>
              {/* Phase 1a hung the payload tables off this tree, so the sentence
                  that used to say none of them was populated is no longer true.
                  What each type carries is not the same, and only one of the
                  three has a screen of its own. */}
              <span className="ml-2 text-xs text-tertiary">
                {node.type === 'document'
                  ? 'The body, its versions, and its review date live on the document page.'
                  : node.type === 'file'
                    ? 'The stored bytes are served through the API, never by object key.'
                    : 'A URL and nothing else — permissions on it cannot be enforced (§4.6).'}
              </span>
            </Definition>
            <Definition term="Space">
              <Link to="/spaces/$spaceId" params={{ spaceId: node.spaceId }}>
                {space.data?.space.name ?? node.spaceId}
              </Link>
            </Definition>
            <Definition term="Parent node">
              {node.parentNodeId ? (
                <Link to="/nodes/$nodeId" params={{ nodeId: node.parentNodeId }}>
                  View parent
                </Link>
              ) : (
                <span className="text-tertiary">Top level in this space</span>
              )}
            </Definition>
            <Definition term="Owner">
              {owner ? (
                <span className="flex flex-wrap items-center gap-2">
                  <Link to="/admin/people/$personId" params={{ personId: owner.id }}>
                    {owner.name}
                  </Link>
                  <StatusBadge status={owner.status} />
                </span>
              ) : (
                <Mono>{node.ownerId}</Mono>
              )}
            </Definition>
            <Definition term="Position">{node.position}</Definition>
            <Definition term="Created">{formatDateTime(node.createdAt)}</Definition>
            <Definition term="Identifier">
              <Mono>{node.id}</Mono>
            </Definition>
          </DefinitionList>
        </Card>

        <Card>
          <h2 className="mb-3 text-md font-semibold text-primary">Children</h2>
          {children.length === 0 ? (
            <p className="text-sm text-tertiary">
              No child nodes. Deleting this node would soft-delete it alone.
            </p>
          ) : (
            <>
              <p className="mb-3 text-sm text-tertiary">
                Deleting this node soft-deletes all {children.length} of these too, in the same
                transaction.
              </p>
              <ul className="flex flex-col gap-2">
                {children.map((child) => (
                  <li key={child.id} className="flex items-center gap-2 text-sm">
                    <Link to="/nodes/$nodeId" params={{ nodeId: child.id }}>
                      {child.title}
                    </Link>
                    <Badge tone="outline">{child.type}</Badge>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
      </div>

      <GrantsPanel subjectType="node" subjectId={node.id} subjectLabel={node.title} />

      <Dialog
        title="Reassign ownership"
        description={`Currently owned by ${owner?.name ?? node.ownerId}.`}
        isOpen={reassigning}
        onOpenChange={setReassigning}
      >
        {(close) => <ReassignForm nodeId={node.id} onClose={close} />}
      </Dialog>
    </>
  );
}
